// ✅ src/pages/VehicleV2/VehicleFuelRate.jsx  (FULL FILE)
// Listing navigation:
//   navigate(`/vehicle/VehicleFuelRate?fuelRateId=${fuelRateId}`);
//
// Backend mount:
//   app.use("/api/fuelRate", fuelRate);
//
// APIs used:
//   GET  https://nissi-india.com/api/fuelRate/fuel-types
//   GET  https://nissi-india.com/api/fuelRate/view?fuelRateId=123
//   POST https://nissi-india.com/api/fuelRate/save
//
// ✅ Updated to match listing pattern:
// - Uses common localStorage header pattern
// - Reads loginUserId from session/local storage
// - Supports add/edit mode
// - Save disabled if branch inactive / no add rights
// - Same UI style pattern as listing page
// - Back button goes to /vehicle/vehiclefuelrateListing

import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft, FaSave } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/fuelRate";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const dateToYMD = (value) => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const VehicleFuelRate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ Shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const branch = localStorage.getItem("branchType") || localStorage.getItem("branchtype") || "";
  const userId = localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const fuelRateId = useMemo(() => {
    return toInt(searchParams.get("fuelRateId"), 0);
  }, [searchParams]);

  // Rights / branch active
  const canAdd = useMemo(() => {
    const v = sessionStorage.getItem("fuelrate_addrights");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const branchActive = useMemo(() => {
    const v = sessionStorage.getItem("BranchActive") || localStorage.getItem("BranchActive");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  // fields
  const [fuelTypeId, setFuelTypeId] = useState("Select");
  const [rate, setRate] = useState("");
  const [effectDate, setEffectDate] = useState("");

  // dropdown
  const [fuelTypes, setFuelTypes] = useState([]);

  // loading
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const inputClass = `w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  /* ------------------------- LOAD FUEL TYPES ------------------------- */
  const loadFuelTypes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/fuel-types`, {
        headers: commonHeaders,
      });

      const nextFuelTypes = Array.isArray(data?.fuelTypes) ? data.fuelTypes : [];
      setFuelTypes(nextFuelTypes);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load fuel types");
      setFuelTypes([]);
    }
  }, [commonHeaders]);

  /* ------------------------- LOAD EDIT DATA ------------------------- */
  const loadEditData = useCallback(async () => {
    if (!fuelRateId) return;

    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/view`, {
        params: { fuelRateId },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load fuel rate");
      }

      const d = data?.data || {};

      setFuelTypeId(d.fuelTypeId != null ? String(d.fuelTypeId) : "Select");
      setRate(d.rate != null ? String(d.rate) : "");
      setEffectDate(dateToYMD(d.effectDate || d.date));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to load fuel rate");
    } finally {
      setPageLoading(false);
    }
  }, [fuelRateId, commonHeaders]);

  useEffect(() => {
    loadFuelTypes();
  }, [loadFuelTypes]);

  useEffect(() => {
    loadEditData();
  }, [loadEditData]);

  const canSave = useMemo(() => {
    if (!branchActive || !canAdd) return false;
    return String(fuelTypeId) !== "Select" && toStr(rate) !== "";
  }, [fuelTypeId, rate, branchActive, canAdd]);

  const handleClear = () => {
    setFuelTypeId("Select");
    setRate("");
    setEffectDate("");
  };

  const handleSave = async () => {
    if (!branchActive) {
      toast.error("Branch is not active. Save not allowed.");
      return;
    }

    if (!canAdd) {
      toast.error("You do not have rights to save fuel rate.");
      return;
    }

    if (String(fuelTypeId) === "Select") {
      toast.error("Please select a fuel type");
      return;
    }

    if (!toStr(rate)) {
      toast.error("Please enter rate");
      return;
    }

    const payload = {
      fuelRateId: fuelRateId || 0,
      fuelTypeId: toInt(fuelTypeId, 0),
      rate: toStr(rate),
      effectDate: effectDate || "",
      loginUserId,
    };

    try {
      setLoading(true);

      const { data } = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to save fuel rate");
      }

      toast.success(
        data?.message || (fuelRateId ? "Fuel rate updated successfully" : "Fuel rate saved successfully")
      );

      setTimeout(() => {
        navigate("/vehicle/vehiclefuelrateListing");
      }, 800);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to save fuel rate");
    } finally {
      setLoading(false);
    }
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE FUEL RATE</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                {fuelRateId ? "Edit Vehicle Fuel Rate" : "Add Vehicle Fuel Rate"}
              </h1>
            </div>

            <button
              type="button"
              onClick={() => navigate("/vehicle/vehiclefuelrateListing")}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FaArrowLeft className="w-4 h-4" />
              Back to Listing
            </button>
          </div>

          {/* Form */}
          <div
            className={`rounded-xl p-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="max-w-2xl mx-auto">
              {/* Fuel Type */}
              <div className="mb-8">
                <label className={labelClass}>Fuel Type</label>
                <select
                  id="fuelTypeId"
                  value={fuelTypeId}
                  onChange={(e) => setFuelTypeId(e.target.value)}
                  className={inputClass}
                  disabled={pageLoading || loading}
                >
                  <option value="Select">Select</option>
                  {fuelTypes.map((x) => (
                    <option key={x.id} value={String(x.id)}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Rate */}
              <div className="mb-8">
                <label className={labelClass}>Rate</label>
                <input
                  id="rate"
                  type="text"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  placeholder="Enter rate..."
                  className={inputClass}
                  disabled={pageLoading || loading}
                />
              </div>

              {/* Effect Date */}
              <div className="mb-8">
                <label className={labelClass}>Effect Date</label>
                <input
                  id="effectDate"
                  type="date"
                  value={effectDate}
                  onChange={(e) => setEffectDate(e.target.value)}
                  className={inputClass}
                  disabled={pageLoading || loading}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleClear}
                  disabled={loading || pageLoading}
                  className="px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                >
                  Clear
                </button>

                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading || pageLoading || !canSave}
                  className={`flex items-center gap-2 px-8 py-3 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    loading || pageLoading || !canSave
                      ? "bg-gray-400 cursor-not-allowed"
                      : `${buttonClass} ${hoverClass}`
                  }`}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave className="w-4 h-4" />
                      {fuelRateId ? "Update Fuel Rate" : "Save Fuel Rate"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className={`mt-8 pt-6 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <p className={`text-sm text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              INFRA SCREEN : VEHICLE FUEL RATE
            </p>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleFuelRate;